import { flexRender } from '@tanstack/react-table';
import { Search, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@avanta/shared/components/Pagination';
import { TableSkeleton } from '@avanta/shared/components/TableSkeleton';
import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avanta/shared/components/ui/Table';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type FilterValue } from '@avanta/shared/types/table';
import { cn } from '@avanta/shared/utils/cn';
import { Text } from '../Text';
import { EmptyContent } from './EmptyContent';
import { MobileCardList } from './components/MobileCardList';
import { SortableTableHeader } from './components/SortableTableHeader';
import { TableColumnFilter } from './components/TableColumnFilter';
import { TableFilterButtons } from './components/TableFilterButtons';
import { TableFilterDateRange } from './components/TableFilterDateRange';
import { type PaginatedTableLayoutProps } from './types';

export const PaginatedTable = <T, K extends FilterValue = FilterValue>({
  table,
  columns,
  isLoading = false,
  emptyMessage = '',
  emptyTitle,
  title,
  subtitle,
  icon,
  tableClassName,
  withSearch = false,
  searchPlaceholder = 'Common.search',
  searchPlaceholderNs,
  filter,
  selectedFilterKey,
  setFilter,
  dateRangeFilter,
  selectedDateRange,
  setDateRange,
}: PaginatedTableLayoutProps<T, K>): React.ReactElement => {
  const { getHeaderGroups, getRowModel, getState, setGlobalFilter } = table;
  const rows = getRowModel().rows;
  const hasRows = rows.length > 0;
  const globalFilter = getState().globalFilter;
  const Icon = icon;
  const { t } = useTranslation(LOCIZE_NS.COMMON);

  const showFilters = Boolean(filter && filter.options.length > 0 && setFilter);
  const showDateRangeFilter = Boolean(dateRangeFilter && setDateRange);

  const tableContent = (
    <div className="flex flex-col gap-6">
      {(withSearch || showFilters || showDateRangeFilter) && (
        <div className="flex flex-col-reverse gap-4 am-lg:flex-row am-lg:items-center am-lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
            {showFilters && filter && setFilter && (
              <TableFilterButtons
                options={filter.options}
                selectedKey={selectedFilterKey}
                onChange={setFilter}
                allLabel={filter.allLabel}
              />
            )}
            {showDateRangeFilter && dateRangeFilter && setDateRange && (
              <TableFilterDateRange value={selectedDateRange} onChange={setDateRange} />
            )}
          </div>
          {withSearch && (
            <div className="max-w-xs max-am-lg:max-w-full flex-1 flex items-center gap-2">
              <Input
                leftIcon={Search}
                rightSlot={
                  <Button
                    variant="ghost"
                    icon="only"
                    onClick={() => setGlobalFilter('')}
                    className={cn('hover:bg-transparent text-denim-900', !globalFilter && 'hidden')}
                  >
                    <XIcon className="size-6" />
                  </Button>
                }
                placeholder={t(
                  searchPlaceholder,
                  searchPlaceholderNs ? { ns: searchPlaceholderNs } : {}
                )}
                value={globalFilter}
                onChange={e => setGlobalFilter(e.target.value)}
                className="w-full shrink-0"
              />
            </div>
          )}
        </div>
      )}
      <MobileCardList
        rows={rows}
        headerGroups={getHeaderGroups()}
        isLoading={isLoading}
        columnsCount={columns.length}
        emptyMessage={emptyMessage}
        emptyTitle={emptyTitle}
      />
      <div className="hidden am-lg:block rounded-3xl border border-teal-300 overflow-x-auto px-4 pb-4">
        <Table className={tableClassName}>
          <TableHeader>
            {getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    <div className="inline-flex items-center gap-1">
                      <SortableTableHeader header={header} />
                      <TableColumnFilter column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton columns={columns.length} />
            ) : hasRows ? (
              rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="border-none">
                  <EmptyContent emptyTitle={emptyTitle} emptyMessage={emptyMessage} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination table={table} />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="size-8 shrink-0 text-denim-500" strokeWidth={1.5} />}
        <div>
          {title && (
            <Text tag="h3" variant="heading-sm-medium">
              {title}
            </Text>
          )}
          {subtitle && (
            <Text tag="h3" variant="text-xs-regular" className="text-gray-400">
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      {tableContent}
    </div>
  );
};
